import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_LPCO_INFO_R, SG_LPCO_ASINCTRA_LIST_U } from "./services/OsciCfgnAsinService";
import { CommonErrors } from "@/comn/components";
import { lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Modify LPCO Assignment
 * !== LPCO Assignment 수정 ==!
 */
export const OSCI0203016S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });

    const { getResource } = useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0116" },
            { area: "comnCd", comnCd: "OSC0120" },
            { area: "comnCd", comnCd: "OSC0038" },
            { area: "comnCd", comnCd: "OSC0119" },
            { area: "comnCd", comnCd: "OSC0113" },
        ],
    });

    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { appFmId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [appAsinList, setAppAsinList] = useState(comnUtils.getGridData([]));

    // original data
    const orginDataRef = useRef({ info: {}, asinList: [] });

    const OFFICER_ROLE = [
        // "", // NONE:
        // "VE", // VRFN:
        // "VA", // VRFN_APRV:
        "FP", // ACCT:
        "IP", // INSC:
        // "FR", // FNAL_APRV:
        // "FE", // EXMPN_VRFN:
        // "ES", // EXMPN_VRFN_APRV:
        // "EF", // EXMPN_FNAL_APRV:
    ];

    // const BASE_EXCLUDES = ["0", "C", "R", "B"];
    const BASE_EXCLUDES = ["0", "C"];

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        lpcoInfoRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_R,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Component List !== Component 목록 ==!
        appAsinList: useGrid({
            defaultSchema: SG_LPCO_ASINCTRA_LIST_U,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_AppAsinList: {
            head: {},
            cell: {},
            edit: {
                asinMetdCd: ({ rowValues, control }: any) => {
                    const roleCd = rowValues?.asnmtOfcrTpCd;
                    const isOfcrRole = OFFICER_ROLE.includes(roleCd);
                    // console.log("rowValues", rowValues, "isOfcrRole", isOfcrRole);
                    const excludes = isOfcrRole ? BASE_EXCLUDES : [...BASE_EXCLUDES, "V"];
                    return <FormControl {...control.props} excludes={excludes} />;
                },
            },
            row: null,
            checkbox: null,
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        /* save */
        saveAppAsinList: useFetch({
            api: (data) => APIS.saveAppAsinList({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0203015s + "/" + appFmId); //data.appSaveInfo.content.appFmId
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203015s + "/" + appFmId);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.lpcoInfoRgsr.setErrors(error.response.data.errors);
                }

                const errMsg = error?.response?.data?.errorMessage || "msg.osc.00015";
                modal.openModal({
                    content: errMsg,
                });
            },
        }),
        getAppDtl: useFetch({
            api: async (data) => await APIS.getAppDtl(appFmId),
            enabled: !!appFmId,
            onSuccess: (data) => {
                console.log("getAppDtl onSuccess ==================== ", data);
                form.lpcoInfoRgsr.setValues(data.appInfo.content?.info);
                setAppAsinList(comnUtils.getGridData(data.appInfo.content?.asinList || []));

                // orginDataRef.current.info = { ...data.appInfo?.content.info } || {};
                // orginDataRef.current.asinList = Array.from(data.appInfo.content?.asinList);
            },
            onError: (error) => {},
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    const handler = {
        // save
        saveAppAsinList: form.lpcoInfoRgsr.handleSubmit(async () => {
            let appAsinList = grid.appAsinList.getViewData();

            let bCheck = true;
            if (!appAsinList || appAsinList.length == 0) {
                // Please enter one or more Assignment Criteria (methods). !== 배부 기준(방법)을 하나 이상 입력하세요. ==!
                bCheck = false;
                modal.openModal({
                    content: "msg.osc.00024",
                });
            } else {
                // grid validate
                let errResult = grid.appAsinList.validate() || {};
                errResult["errors"] = errResult?.errors || [];

                const duplMap: { [key: string]: boolean } = {};
                let idx = 1;
                console.log("appAsinList ", appAsinList);
                for (const item of appAsinList) {
                    const val = item.asnmtOfcrTpCd;
                    if (val && duplMap[val]) {
                        const strLabel = await lpcoUtils.getCodeLabel(getResource("comnCd", "OSC0119"), val);
                        // const strLabel = await lpcoUtils.getCodeLabel("OSC0119", val);
                        errResult.errors.push(lpcoUtils.getErrorObj(idx, "L_OFCR_ROLE", "msg.osc.00017", strLabel));
                    } else {
                        duplMap[val] = true;
                    }
                    idx++;
                }

                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                } else {
                    const saveAppAsinList = grid.appAsinList.getData();
                    const data = { ...form.lpcoInfoRgsr.getValues(), asinList: saveAppAsinList };
                    modal.openModal({
                        content: "msg.osc.00090" /**Do you want to save ? */,
                        onConfirm: () => {
                            fetch.saveAppAsinList.fetch(data);
                        },
                    });
                }
            }
        }),
        click_Btn_AddGridList: () => {
            grid.appAsinList.addRow();
        },
        click_Btn_DeleteGridList: () => {
            const seltList: any[] = grid.appAsinList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.appAsinList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.lpcoAtchFileList.deleteRow("checkbox");
            //     },
            // });
        },
        refreshOrgCdList: async () => {
            console.log("refreshOrgCdList");
            setOrgCdList((await lpcoUtils.getOrgCdList()) || []);
        },
        refreshOrgLpcoList: async (usrOrgCd: any) => {
            console.log("refreshOrgLpcoList");
            if (usrOrgCd) setOrgLpcoList((await lpcoUtils.getOrgLpcoByPrcsList(usrOrgCd)) || []);
            else setOrgLpcoList([]);
        },
        setAppDtl: async (appFmId: any) => {
            fetch.getAppDtl.fetch(appFmId);
        },
    };

    useEffect(() => {
        handler.setAppDtl(appFmId); // lpco app info
    }, []);

    return (
        <Page
        // id={pgeUid}
        // title={t("T_MDFY_ASNMT_CTRA_METD")}
        // navigation={{
        //     base: comnEnvs.base,
        //     nodes: [
        //         ...BASE.nodes,
        //         { path: "/lpco/cfgn/asin/osci0203014q", label: "T_ASNMT_CTRA_METD_LST" },
        //         { path: "/lpco/cfgn/asin/osci0203016s/" + appFmId, label: "T_MDFY_ASNMT_CTRA_METD" },
        //     ],
        // }}
        >
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.orgNm} options={orgCdList}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoRgsr.schema.appNm}
                                options={orgLpcoList}
                                onChange={(ev: any) => {
                                    console.log(ev);
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_ASNMT_CTRA_METD"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.click_Btn_AddGridList();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.appAsinList.grid}
                        data={appAsinList} /* fetch.getRpckItmAppList.data?.appAsinList */
                        render={render.grid_AppAsinList}
                        // onCellClick={handler.click_Grid_RpckItmAppList}
                        // onRowClick={handler.click_GridRow_RpckItmAppList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203014q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            //form.lpcoInfoRgsr.reset({ ...orginDataRef.current.info });
                            grid.appAsinList.resetData();
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.saveAppAsinList}></Button>
                    <Button role="cancel" onClick={() => navigate(`${URLS.osci0203015s}/${appFmId}`)}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
